import * as WillCallEmail from './WillCallEmail';
import * as EmailTemplate from './EmailTemplate';
import { VPResult } from '../VPResult';
import * as VPErrors from '../../VPError';
import { ApolloClient, gql } from '@apollo/client/core';
import { Mutation } from '../../generated/graphql';
import { VP_ERROR_FIELDS } from '../VPError/fragments';
import { Query } from '../../generated/bo.graphql';

export const SEND_WILL_CALL_EMAILS_MUTATION = gql`
  mutation SendWillCallEmails($input: SendWillCallEmailsMutationInput!) {
    sendWillCallEmails(input: $input) {
      success
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

export const EMAIL_TEMPLATES_QUERY = gql`
  query EmailTemplates {
    willCallEmailTemplates {
      id
      templateName
      subject
      content
      bcc
    }
  }
`;

export const SAVE_EMAIL_TEMPLATE_MUTATION = gql`
  mutation SaveEmailTemplate($input: SaveEmailTemplateMutationInput!) {
    saveEmailTemplate(input: $input) {
      emailTemplate {
        id
        templateName
        subject
        content
        bcc
      }
      errors { ...VPErrorFields }
    }
  }
  ${VP_ERROR_FIELDS}
`;

export const DELETE_EMAIL_TEMPLATE_MUTATION = gql`
  mutation DeleteEmailTemplate($input: DeleteEmailTemplateMutationInput!) {
    deleteEmailTemplate(input: $input) {
      success
      errors { ...VPErrorFields }
    }
  }
  ${VP_ERROR_FIELDS}
`;

type SendWillCallEmailsMutationInput = {
  clientMutationId?: string;
  willCallEmails: WillCallEmail.SendWillCallEmailsInput;
};

export const sendWillCallEmail = async (
  client: ApolloClient<Mutation>,
  params: WillCallEmail.SendWillCallEmailsInput,
): Promise<VPResult<void>> => {
  const gqlAttributes = WillCallEmail.toGqlInput(params);
  const mutationInput: SendWillCallEmailsMutationInput = {
    willCallEmails: gqlAttributes as WillCallEmail.SendWillCallEmailsInput,
  };

  const { data } = await client.mutate({
    mutation: SEND_WILL_CALL_EMAILS_MUTATION,
    variables: {
      input: mutationInput,
    },
  });

  const response = data?.sendWillCallEmails;

  if (response.success) {
    return {
      success: true,
      data: undefined,
    };
  }

  return {
    success: false,
    errors: VPErrors.fromGqlCollection(response?.errors),
  };
};

export const fetchTemplates = async (client: ApolloClient<Query>): Promise<EmailTemplate.EmailTemplate[]> => {
  const { data } = await client.query({
    query: EMAIL_TEMPLATES_QUERY,
    fetchPolicy: 'network-only',
  });
  return EmailTemplate.fromGqlCollection(data?.willCallEmailTemplates || []);
};

export const saveEmailTemplate = async (
  client: ApolloClient<Mutation>,
  params: EmailTemplate.EmailTemplateAttributes,
): Promise<VPResult<EmailTemplate.EmailTemplate>> => {
  const { data } = await client.mutate({
    mutation: SAVE_EMAIL_TEMPLATE_MUTATION,
    variables: { input: { emailTemplate: params } },
  });

  const response = data?.saveEmailTemplate;
  if (!response.emailTemplate) {
    return { success: false, errors: VPErrors.fromGqlCollection(response.errors) };
  }
  return { success: true, data: EmailTemplate.fromGql(response.emailTemplate) };
};

export const deleteEmailTemplate = async (
  client: ApolloClient<Mutation>,
  id: number,
): Promise<VPResult<void>> => {
  const { data } = await client.mutate({
    mutation: DELETE_EMAIL_TEMPLATE_MUTATION,
    variables: { input: { emailTemplateId: id } },
  });

  const response = data?.deleteEmailTemplate;
  if (!response.success) {
    return { success: false, errors: VPErrors.fromGqlCollection(response.errors) };
  }
  return { success: true, data: undefined };
};
